import React, { FC, useRef } from "react";

import { BaseComponentProps } from "@keepeek/refront-components";
import { Box, IconButton } from "@mui/material";
import { ConnectableElement, useDrag, useDrop } from "react-dnd";
import { useDebounce } from "use-debounce";

import { KIcon } from "../../../../components/common/KIcon";
import { Template } from "../../../../models/configuration/definitions/template";
import StyledFrontEditTemplateWrapper, { classes } from "./index.styled";

export type FrontEditTemplatesWrapperProps = {
  indexT: number;
  hovered: boolean;
  setHovered: (hovered: number | undefined) => void;
  sectionList: Template[];
  loading: boolean;
  handleInvertSections: (firstSectionId: number, secondSectionId: number) => void;
  handleReorderSections: (fromId, toId) => void;
  children: any;
} & BaseComponentProps;

const FrontEditTemplatesWrapper: FC<FrontEditTemplatesWrapperProps> = function ({
  indexT,
  children,
  hovered,
  setHovered,
  sectionList,
  loading,
  handleInvertSections,
  handleReorderSections,
}) {
  const ref = useRef<HTMLDivElement>(null);

  const dragType = "Template";
  const [, drag] = useDrag(
    () => ({
      type: dragType,
      item: {
        index: indexT,
        section: sectionList[indexT],
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [sectionList, indexT],
  );

  const [debouncedHovered] = useDebounce(hovered, 0);

  const [, drop] = useDrop(
    () => ({
      accept: dragType,
      hover(_draggedKey: any) {
        setHovered(indexT);
      },
      drop(draggedKey: any) {
        if (draggedKey.index !== indexT) {
          const fromIndex = draggedKey.index;
          handleReorderSections(fromIndex, indexT);
        }
        setHovered(undefined);
      },
    }),
    [sectionList, indexT],
  );

  if (!loading) {
    drag(drop(ref as ConnectableElement));
  }

  return (
    <StyledFrontEditTemplateWrapper
      key={indexT}
      ref={ref}
      sx={{
        zIndex: debouncedHovered ? 5000 : "inherit",
        border: (theme) =>
          debouncedHovered ? `1px solid ${theme.palette.primary.main}` : "inherit",
        boxShadow: (theme) =>
          debouncedHovered ? `0px 0px 20px ${theme.palette.primary.main}` : "inherit",
      }}
    >
      <Box key={""} className={classes.templateSection}>
        {children}
      </Box>

      {!loading && (
        <Box className={classes.actionButtonContainer}>
          <Box className={classes.actionButton}>
            <span>
              <IconButton
                disabled={indexT === 0}
                onClick={() => handleInvertSections(indexT, indexT - 1)}
              >
                <KIcon
                  sx={{
                    fontSize: (theme) => theme.typography.fontSize || "0.8rem",
                  }}
                  className="fa-arrow-up"
                />
              </IconButton>
            </span>
            <span>
              <IconButton
                disabled={indexT === sectionList.length - 1}
                onClick={() => handleInvertSections(indexT, indexT + 1)}
              >
                <KIcon
                  sx={{
                    fontSize: (theme) => theme.typography.fontSize || "0.8rem",
                  }}
                  className="fa-arrow-down"
                />
              </IconButton>
            </span>
          </Box>
        </Box>
      )}
    </StyledFrontEditTemplateWrapper>
  );
};

export default FrontEditTemplatesWrapper;
